import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import {
  CrowdinButton,
  Icon,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  useNavMenuSidebar
} from '@anytag/shared-atoms';
import { Logo } from '@anytag/shared-molecules';
import { useGlobalLayoutProps } from '@anytag/shared-hooks';
import { MenuTitle } from '@anytag/router';
import { useAuthData, useUserRoles } from '@anytag/auth';
import { cn } from '@anytag/shared-utils';
import { MenuGroup, MenuItem, useMenuConfigs } from './menu';
import { getCmsMenuItems, hashtagsMenuItems, tiktokMenuItems } from './menu/utils';

export interface NavMenuSidebarProps {
  activeTitle?: MenuTitle;
  type?: 'cms' | 'hashtags';
}

export const NavMenuSidebar = ({ activeTitle, type }: NavMenuSidebarProps) => {
  const { auth } = useAuthData();
  const { menuItems, permissionLoading } = useMenuConfigs();
  const { updateGlobalLayout } = useGlobalLayoutProps();
  const { isAdvertiserFromTikTok, isAdminStaff } = useUserRoles();
  const navigate = useNavigate();
  const { isOpen, setSidebarState } = useNavMenuSidebar();

  const hasDisabledNavigation = isAdvertiserFromTikTok && auth.subscriptionPaymentFailed;
  const allAvailableMenuItems = (() => {
    if (isAdvertiserFromTikTok) return tiktokMenuItems;

    if (type === 'cms') return getCmsMenuItems(isAdminStaff);

    if (type === 'hashtags') return hashtagsMenuItems;

    return menuItems;
  })();

  useEffect(() => {
    if (hasDisabledNavigation) {
      navigate({ to: '/tiktok/failed-payment' });
    }
    updateGlobalLayout({ hasFailedSubscriptionPayment: !!hasDisabledNavigation });
  }, [hasDisabledNavigation]);

  return (
    <Sidebar name="nav-menu" variant="sidebar" collapsible="icon" className="z-10 h-screen">
      <SidebarHeader className={cn('flex-row justify-between pb-2', !isOpen && 'px-2')}>
        <SidebarMenu>
          <SidebarMenuItem className="flex flex-row items-center justify-between">
            {isOpen ? <Logo type="black" to={auth.defaultRoute} className="h-6 w-auto" /> : null}
            <Icon
              icon="bar-left"
              width={24}
              height={24}
              onClick={() => setSidebarState({ isOpen: !isOpen })}
              variant="gray"
              className="cursor-pointer hover:opacity-80"
            />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      {isOpen ? (
        <SidebarContent className="p-2 aria-disabled:opacity-60" aria-disabled={permissionLoading}>
          {allAvailableMenuItems.map((item, i) =>
            item?.subMenus?.length ? (
              <MenuGroup key={i} {...item} activeTitle={activeTitle} disabled={hasDisabledNavigation} />
            ) : ![MenuTitle.ReleaseNotes, MenuTitle.HelpCenter].includes(item.title as MenuTitle) ? (
              <MenuItem
                key={i}
                {...item}
                activeTitle={activeTitle}
                activeIcon={item.title === activeTitle}
                disabled={hasDisabledNavigation}
              />
            ) : null
          )}
        </SidebarContent>
      ) : null}
      {isOpen ? (
        <SidebarFooter>
          <SidebarMenu>
            {process.env.NODE_ENV === 'development' && (
              <MenuItem
                key="__asset-preview"
                keyPrefix=""
                rightNode="🏗️"
                title={MenuTitle.AssetPreview}
                activeIcon={MenuTitle.AssetPreview === activeTitle}
                to="/asset-preview"
                size={22}
              />
            )}

            <SidebarMenuItem>
              <CrowdinButton />
            </SidebarMenuItem>
            {/*<UserPanel />*/}
          </SidebarMenu>
        </SidebarFooter>
      ) : null}
    </Sidebar>
  );
};
